import { forwardRef, Fragment, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { BaseButton } from '../Button/__internal__';
import { Popover } from '../Popover';
import { withLabels } from '../withLabels/withLabels';
import { DefaultColorPicker } from './DefaultColorPicker';
import { useColorPickerA11yInfo, useValidatedColorValues } from './hooks';
import bundle from './NovaComponents-ColorPicker-gui-icu.properties';
import cn from './ColorPicker.css.json';
import './ColorPicker.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const ColorPicker = /*#__PURE__*/forwardRef((props, ref) => {
  const localize = useTextLocalizer();
  const {
    selectedColor,
    lastSelectedColor
  } = useValidatedColorValues(props.selectedColor);
  const [popoverIsOpen, setPopoverIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const buttonRef = useRef(null);
  const getColorPickerA11yInfo = useColorPickerA11yInfo();
  const {
    ariaLabel,
    tooltip
  } = getColorPickerA11yInfo({
    selectedColor,
    ariaLabel: props.ariaLabel,
    tooltip: props.tooltip,
    readOnly: props.readOnly
  });
  const dt = useDesignTokens();
  const tokensNoColorChip = {
    bd: dt.tokens['color-neutral'],
    'mark-bg': dt.tokens['color-status-alert'],
    bg: dt.themeVariant === 'high-contrast' ? dt.tokens['color-action-background-control-normal'] : 'transparent'
  };
  const colorswatchButtonTokens = {
    'hover-bd': dt.tokens['color-action-border-subtle-hover'],
    'hover-bg': dt.tokens['color-action-background-subtle-hover'],
    'active-bd': dt.tokens['color-action-border-subtle-selected'],
    'active-bg': dt.tokens['color-action-background-subtle-selected'],
    'disabled-bd': dt.tokens['color-action-border-subtle-disabled'],
    'disabled-bg': dt.tokens['color-action-background-subtle-disabled'],
    'inner-bd': dt.tokens['color-neutral'],
    'border-radius': dt.tokens['size-border-radius-xxs'],
    pd: dt.tokens['space-base-xs'],
    'bd-width': dt.tokens['size-border-width-s'],
    bg: dt.tokens['color-action-background-subtle-normal'],
    bd: dt.tokens['color-action-border-subtle-normal']
  };
  const style = {
    ...renderInlineCssVars(colorswatchButtonTokens, 'colorpicker-swatch'),
    ...renderInlineCssVars(tokensNoColorChip, 'colorpicker-no-color-swatch')
  };
  const handleDismiss = () => {
    setPopoverIsOpen(false);
    setTimeout(() => buttonRef.current?.focus(), 0);
  };
  const handleColorSelection = color => {
    if ('onColorChange' in props) {
      props.onColorChange?.(color);
    }
    handleDismiss();
  };
  const MultiColorSwatchComponent = props.renderAutomaticSwatch ? props.renderAutomaticSwatch({
    getAttributes() {
      const multiColorAriaLabel = `${localize('selectColorAutomatic.label')} ${localize('automatic.label')} (${localize('multipleSmallCaseColors.label')})`;
      const multiColorTitle = `${localize('selectColorAutomatic.label')} ${localize('automatic.label')} (${localize('multipleSmallCaseColors.label')})`;
      return {
        ariaLabel: multiColorAriaLabel,
        title: multiColorTitle
      };
    }
  }) : null;
  const setButtonRef = element => {
    if (element) {
      buttonRef.current = element;
      setRef(ref, element);
    }
  };
  useEffect(() => {
    const handleResize = () => {
      if (activeIndex === 1 && !!popoverIsOpen) {
        setPopoverIsOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [activeIndex, popoverIsOpen]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(BaseButton, {
      id: props.id,
      disabled: props.disabled,
      ref: setButtonRef,
      ...(props.readOnly && {
        role: 'img'
      }),
      ...(!(props.selectedColor.type === 'automatic' && props.renderAutomaticSwatch) && {
        tooltip
      }),
      ...(!(props.selectedColor.type === 'automatic' && props.renderAutomaticSwatch) && {
        ariaLabel
      }),
      ariaDescribedBy: props.ariaDescribedBy,
      onPress: () => {
        if (!props.readOnly) {
          setPopoverIsOpen(true);
        }
      },
      className: cx(cn.dropdownColorSwatch, selectedColor.color === '' && cn.noColorSwatch, props.disabled && cn.disabled, props.readOnly && cn.readOnly, props.className),
      style: {
        ...style,
        ...props.style
      },
      testId: postfixTestId(props.testId, 'colorSwatch'),
      tokens: () => ({
        filter: 'none'
      }),
      children: props.selectedColor.type === 'automatic' && props.renderAutomaticSwatch ? MultiColorSwatchComponent : /*#__PURE__*/_jsx("span", {
        className: cn.dropdownColorSwatchInner,
        style: {
          background: selectedColor.color
        },
        "data-testid": postfixTestId(props.testId, 'color-swatch-inner')
      })
    }), /*#__PURE__*/_jsx(Popover, {
      role: "dialog",
      className: cn.popover,
      isOpen: popoverIsOpen,
      isModal: activeIndex === 1,
      referenceElementRef: buttonRef,
      ariaLabel: localize('popper.aria'),
      onClose: () => setPopoverIsOpen(false),
      focusLockProps: props.focusLockProps,
      testId: postfixTestId(props.testId, 'popover'),
      children: /*#__PURE__*/_jsx(DefaultColorPicker, {
        ...props,
        testId: postfixTestId(props.testId, 'defaultColorPicker'),
        renderAutomaticSwatch: props.renderAutomaticSwatch,
        selectedColor: selectedColor,
        automaticColor: props.automaticColor,
        newColor: selectedColor,
        lastSelectedColor: lastSelectedColor,
        onColorSelect: handleColorSelection,
        selectedIndex: activeIndex,
        onTabItemSelect: setActiveIndex,
        onCancelPress: handleDismiss
      })
    })]
  });
});
const CPwithLabels = withLabels(ColorPicker);
export const ColorPickerWithLabels = /*#__PURE__*/forwardRef((props, ref) => {
  const getColorPickerA11yInfo = useColorPickerA11yInfo();
  const {
    ariaLabel
  } = getColorPickerA11yInfo(props);
  return /*#__PURE__*/_jsx(CPwithLabels, {
    ...props,
    ref: ref,
    ariaLabel: ariaLabel
  });
});
//# sourceMappingURL=ColorPicker.js.map